import { createContext, useContext } from 'react';
import type { Currency, FxTable, IsoDate, Money } from '@budget/core';

export type FxStatus = 'loading' | 'ready' | 'error';

export interface CurrencyState {
  displayCurrency: Currency;
  setDisplayCurrency: (currency: Currency) => void;
  fxTable: FxTable | null;
  fxStatus: FxStatus;

  /**
   * Convert into the display currency, or throw.
   *
   * `on` picks the rate date: spending converts at the transaction's own rate,
   * portfolio value at the latest one. Omit it for "today".
   *
   * The result is **always** in the display currency. It never falls back to
   * returning the input unchanged — a caller that then sums the results would
   * silently mix currencies, and a net-worth figure that is quietly wrong is
   * worse than one that is visibly missing.
   */
  convert: (value: Money, on?: IsoDate) => Money;

  /** Same, but null when there is no usable rate. Use this to aggregate. */
  tryConvert: (value: Money, on?: IsoDate) => Money | null;

  /**
   * Convert and format for display. When the rate is missing it formats the
   * original amount in its own currency, which is honest for a single cell —
   * unlike a total, one value carries its own label.
   */
  format: (value: Money, on?: IsoDate) => string;
}

export const CurrencyContext = createContext<CurrencyState | null>(null);

export function useCurrency(): CurrencyState {
  const value = useContext(CurrencyContext);
  if (!value) throw new Error('useCurrency must be used inside <CurrencyProvider>');
  return value;
}
